/**
 * KPI tile — the mock's `.kpi` family (`recommandations.html`, 2026-10-07).
 *
 * Three shapes, one component, because they share a row and must align:
 *  - plain: uppercase 11px label (with its family dot) + 26px figure + 12.5px note
 *  - `bar`: the same, with an 8px progress track under the figure in its own colour
 *  - `hero`: violet fill, white ink (the mock's `.kpi.hero`)
 *
 * The score tile uses `bar` like the rate tiles beside it. There WAS a `ring` shape
 * (a 56px donut carrying the « / 100 »), dropped 2026-10-08: it made the score the
 * odd one out on a row of otherwise identical tiles, and the figure inside a 56px
 * ring can only be 18px — so the most important number read as the least.
 *
 * The figure stays DELIBERATELY proportional (no tabular-nums): a large standalone
 * number looks loose in tabular figures. Column alignment is `VizTable`'s job.
 *
 * The delta always carries an arrow glyph + the caller's suffix, so colour is never
 * the only channel. NO SPARKLINE (removed 2026-10-05): « Présence dans le temps »
 * plots the same series on the same screen with a real axis, so a sparkline was a
 * strictly worse duplicate — and it auto-scales min→max, which turns a
 * two-measured-day period into a confident diagonal.
 */

import { Box, Paper, Typography, type SxProps, type Theme } from '@mui/material';
import { CARD, VIZ, fmtDelta } from './tokens';

export interface StatTileProps {
  label: string;
  value: string;
  /** Family dot before the label. Omit for no dot. */
  dot?: string;
  delta?: { value: number; goodUp?: boolean; suffix?: string };
  foot?: React.ReactNode;
  /**
   * Progress track under the figure. `value`/`max` (default 100) in the caller's own
   * unit. Pass it only for a MEASURED value: an empty track reads as a real zero.
   */
  bar?: { value: number; max?: number; color?: string };
  hero?: boolean;
  sx?: SxProps<Theme>;
}

export default function StatTile({
  label,
  value,
  dot,
  delta,
  foot,
  bar,
  hero,
  sx,
}: StatTileProps) {
  const d = delta ? fmtDelta(delta.value, delta.goodUp ?? true) : null;
  const ink = hero ? '#fff' : VIZ.ink;
  const labelInk = hero ? '#E7DDFD' : VIZ.ink2;
  const footInk = hero ? '#E7DDFD' : VIZ.muted;

  const labelEl = (
    <Typography
      component="div"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: labelInk,
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', bgcolor: hero ? '#fff' : dot }}
        />
      )}
      {label}
    </Typography>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: '14px 18px',
        borderRadius: `${CARD.tileRadius}px`,
        border: `1px solid ${hero ? VIZ.violet : VIZ.border}`,
        bgcolor: hero ? VIZ.violet : undefined,
        boxShadow: 'none',
        // Equal height whatever the shape: the tiles share a row, and a short one
        // reads as a less important measurement rather than a shorter one.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.75,
        ...sx,
      }}
    >
      {labelEl}
      <Typography
        component="div"
        sx={{
          fontSize: hero ? 34 : 26,
          lineHeight: 1.15,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: ink,
          // deliberately NOT tabular-nums
        }}
      >
        {value}
      </Typography>
      {bar && (
        <Box sx={{ height: 8, bgcolor: VIZ.track, borderRadius: '99px', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'block',
              height: '100%',
              borderRadius: '99px',
              width: `${Math.max(0, Math.min(1, bar.value / (bar.max ?? 100))) * 100}%`,
              bgcolor: bar.color ?? VIZ.violet,
            }}
          />
        </Box>
      )}
      {d && (
        <Typography component="div" sx={{ fontSize: 12, color: hero ? '#fff' : d.color }}>
          {d.text}
          {delta?.suffix ? ` ${delta.suffix}` : ''}
        </Typography>
      )}
      {foot && <Typography sx={{ fontSize: 12.5, lineHeight: 1.4, color: footInk }}>{foot}</Typography>}
    </Paper>
  );
}
